import { Link } from 'react-router-dom';
import AboutUsSEO from '../components/seo/AboutUsSEO';
import backgroundImage from '../static/background.png';
import RotatingIcon from "../components/icons/RotatingIcon";
import JumpingIcon from "../components/icons/JumpingIcon";
import { FaReact } from "react-icons/fa";
import { FaMobileAlt } from "react-icons/fa";
import { IoLogoJavascript } from "react-icons/io";
import { TbBrandTypescript } from "react-icons/tb";
import { BsFiletypeCss } from "react-icons/bs";
import { FaCode } from "react-icons/fa6";
import { MdDataObject } from "react-icons/md";
import { DiScrum } from "react-icons/di";

const AboutUs = () => {
  return (
    <main className="min-h-screen">
      <AboutUsSEO />

      {/* Hero Section with Background Image */}
      <div
        className="relative overflow-hidden"
        style={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        <div className="container mx-auto px-4 py-16 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-extrabold text-white mb-6 animate-fade-in drop-shadow-lg">
              About The Web Dev Interview
            </h1>
            <p className="text-xl text-orange-300 leading-relaxed drop-shadow-lg font-semibold">
              Real questions from real interviews, designed to help you succeed
            </p>
          </div>
        </div>
      </div>

      {/* Main Content with Default Background */}
      <div className="container mx-auto px-4 py-12 bg-gray-50">
        <div className="max-w-5xl mx-auto space-y-8">

          {/* Mission Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8 border-l-4 border-green-700">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-gradient-to-br from-green-600 to-emerald-700 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Our Mission</h2>
                <p className="text-gray-700 leading-relaxed text-lg">
                  Welcome to The Web Dev Interview. <br />The idea of the site is quiet simple: we go to real interviews in order to collect actual interview questions,
                  as currently most platforms use AI generated questions that do not reflect real world scenarios.
                </p>
              </div>
            </div>
          </div>

          {/* Value Proposition Card */}
          <div className="bg-gradient-to-br from-green-600 to-emerald-600 rounded-2xl shadow-xl p-8 transform text-white">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold mb-4">Why We're Different</h2>
                <p className="text-white text-opacity-95 leading-relaxed text-lg">
                  Most preparation materials don't cover the wider amount of topics that are actually asked in interviews.
                  Our goal is to help web developers prepare by providing a comprehensive collection of real interview questions and answers.
                </p>
              </div>
            </div>
          </div>

          {/* Topics Grid */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">Core Interview Topics</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { name: 'JavaScript', icon: <JumpingIcon size={40} icon={IoLogoJavascript} />, color: 'from-yellow-400 to-orange-500' },
                { name: 'React', icon: <RotatingIcon size={40} icon={FaReact} />, color: 'from-blue-400 to-cyan-500' },
                { name: 'CSS', icon: <JumpingIcon size={40} icon={BsFiletypeCss} />, color: 'from-pink-400 to-rose-500' },
                { name: 'TypeScript', icon: <JumpingIcon size={40} icon={TbBrandTypescript} />, color: 'from-blue-500 to-indigo-600' },
                { name: 'OOP', icon: <RotatingIcon size={40} icon={MdDataObject} />, color: 'from-green-400 to-emerald-500' },
                { name: 'Scrum', icon: <JumpingIcon size={40} icon={DiScrum} />, color: 'from-purple-400 to-violet-500' },
                { name: 'Live Coding', icon: <JumpingIcon size={40} icon={FaCode} />, color: 'from-gray-600 to-gray-800' },
                { name: 'React Native', icon: <JumpingIcon size={40} icon={FaMobileAlt} />, color: 'from-teal-400 to-cyan-600' }
              ].map((topic, index) => (
                <div
                  key={topic.name}
                  className="group relative overflow-hidden rounded-xl p-4 bg-gradient-to-br"
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${topic.color} opacity-10`}></div>
                  <div className="relative text-center">
                    <div className="flex justify-center items-center h-10 mb-2">{topic.icon}</div>
                    <p className="text-sm font-semibold text-gray-700">{topic.name}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-green-700 to-emerald-600 rounded-2xl shadow-xl p-8 text-center text-white">
            <h2 className="text-3xl font-bold mb-4">Ready to Ace Your Interview?</h2>
            <p className="text-lg text-white text-opacity-90 mb-6">
              Start practicing with real questions from actual interviews
            </p>
            <div className="flex justify-center gap-4">
              <Link
                to="/"
                className="inline-block bg-white text-green-700 font-bold py-3 px-8 rounded-full hover:bg-opacity-90 shadow-lg"
              >
                Explore Questions
              </Link>
              <Link
                to="/mock-interview"
                className="inline-block bg-white text-green-700 font-bold py-3 px-8 rounded-full hover:bg-opacity-90 shadow-lg"
              >
                Book a Mock Interview
              </Link>
            </div>
          </div>

        </div>
      </div>
    </main>
  );
};

export default AboutUs;
